// apps/guide/onboarding/Chrome.tsx
import React, { useEffect } from 'react';
import { Header } from '@ui/components/Header';
import { Footer } from '@ui/components/Footer';
import { getSupabaseClient } from "../lib/supabase";
import { useNavigate } from 'react-router-dom';

type Props = {
  children: React.ReactNode;
  stepIndex: number;
  onStepClick?: (i: number) => void;
};

const STEPS = [
  'Basic Info',
  'Client Capacity & Care',
  'Your Experience',
  'Consultation',
  'Complete Profile',
];

export default function Chrome({ children, stepIndex, onStepClick }: Props) {
  const navigate = useNavigate();
  const progressPercent = ((stepIndex + 1) / STEPS.length) * 100;

  // Scroll to top when step changes (navigating between onboarding components)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [stepIndex]);

  const handleLogout = async () => {
    const supabase = getSupabaseClient();
    
    await supabase.auth.signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#FFFCEF] text-[#304031] flex flex-col">
      {/* Header */}
      <Header
        variant="onboarding"
        stepIndex={stepIndex}
        totalSteps={STEPS.length}
        onLogout={handleLogout}
      />

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-x-hidden">
        {/* Progress Section */}
        <div className="w-full px-4 sm:px-16 pt-6 sm:pt-8 mb-6 sm:mb-8">
          {/* Progress label and percentage */}
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs sm:text-sm font-medium uppercase tracking-wide text-[#214D3C]">Progress</span>
            <span className="text-xs sm:text-sm text-[#214D3C]">{Math.round(progressPercent)}% Complete</span>
          </div>

          {/* Progress bar */}
          <div className="h-2 bg-[#E8DFBD] rounded-full overflow-hidden mb-4">
            <div
              className="h-2 bg-[#D29B04] rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {/* Step labels - hidden on mobile, visible on larger screens */}
          <div className="hidden sm:flex justify-between text-sm">
            {STEPS.map((label, i) => (
              <button
                key={label}
                onClick={() => onStepClick?.(i)}
                className={
                  i === stepIndex
                    ? 'text-[#214D3C] font-medium'
                    : 'text-[#214D3C]/50 transition-colors hover:text-[#214D3C]'
                }
              >
                {label}
              </button>
            ))}
          </div>

          {/* Mobile step indicator - show current step name */}
          <div className="sm:hidden text-center">
            <span className="text-xs text-[#214D3C] font-medium">
              Step {stepIndex + 1} of {STEPS.length}: {STEPS[stepIndex]}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="pb-8">{children}</div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}
